import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Sparkles, X, Heart } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Parent-facing modal that fetches the next encouragement in the companion's voice.
 * Rotation state is managed server-side so repeated taps across devices stay in sync.
 *
 * Only rendered when: child.tribe === "thrive_story" AND child.story_assets_ready
 * AND there is at least one encouragement configured. Hidden entirely otherwise.
 */
export default function CompanionEncouragement({ child, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fetchNext = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/encouragement/${child.id}/next`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reach your companion right now.");
    } finally {
      setLoading(false);
    }
  };

  // Fire once on mount — guarded via useEffect so React doesn't re-trigger it
  // on every re-render (that would loop since setLoading(true) is a state update).
  useEffect(() => { fetchNext(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const companionName = (data?.companion_name || child.companion_name || "your companion").trim();
  const photoUrl = child.companion_photo_path ? `${BACKEND_URL}/api/public-files/${child.companion_photo_path}` : null;
  const accent = child.favorite_color || "#8B5E7A";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(30,30,46,0.55)" }} onClick={onClose} data-testid="companion-encouragement-modal">
      <div
        className="w-full max-w-md rounded-3xl p-7 relative"
        style={{ background: "#FFFEF9", border: `2px solid ${accent}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-ink-soft hover:bg-cream transition"
          data-testid="encouragement-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col items-center">
          <div className="relative">
            <div
              className="w-24 h-24 rounded-full overflow-hidden border-4"
              style={{ borderColor: accent, background: "#FBF5FA" }}
            >
              {photoUrl ? (
                <img src={photoUrl} alt={companionName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Sparkles size={32} style={{ color: accent }} />
                </div>
              )}
            </div>
            <div
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: accent, color: "#FFFEF9" }}
            >
              <Heart size={14} fill="currentColor" />
            </div>
          </div>

          <div className="mt-3 text-[11px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>
            A message from
          </div>
          <div className="font-display text-2xl font-bold text-navy mt-0.5" data-testid="encouragement-companion-name">
            {companionName}
          </div>

          {/* Speech bubble */}
          <div className="mt-5 w-full rounded-2xl px-5 py-5 relative" style={{ background: `${accent}12`, border: `1px solid ${accent}33` }}>
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{ background: `${accent}12`, borderLeft: `1px solid ${accent}33`, borderTop: `1px solid ${accent}33` }}></div>
            {loading ? (
              <div className="text-center py-6 text-ink-soft text-sm italic">Shelly is thinking…</div>
            ) : err ? (
              <div className="text-center py-4 text-sm text-ink-mid italic">{err}</div>
            ) : (
              <p
                className="font-display text-lg leading-relaxed text-center text-navy italic"
                data-testid="encouragement-message"
              >
                "{data?.message}"
              </p>
            )}
          </div>

          {data?.total > 1 && !err && (
            <div className="mt-3 text-[11px] uppercase tracking-widest text-ink-soft font-semibold">
              Message {Number(data.idx) + 1} of {data.total}
            </div>
          )}

          <button
            onClick={onClose}
            className="mt-6 px-7 py-2.5 rounded-full text-sm font-semibold transition hover:opacity-90"
            style={{ background: accent, color: "#FFFEF9" }}
            data-testid="encouragement-thanks"
          >
            Thanks, {companionName.split(" ")[0]} 💛
          </button>
        </div>
      </div>
    </div>
  );
}
